import type { Form, FormInsert, FormQuestion, FormUpdate } from '#shared/types'
import type { PaginationMetadata } from '#shared/types/auth'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface FormFilters {
  page?: number
  limit?: number
  search?: string
  status?: string
  isTemplate?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface FormWithQuestions extends Form {
  vzw_form_questions: FormQuestion[]
}

export const useForms = () => {
  /**
   * Get all forms with pagination and filters
   */
  const getForms = async (filters: FormFilters = {}) => {
    try {
      const queryString = buildQueryString(filters)
      const url = `/api/forms${queryString ? `?${queryString}` : ''}`

      return await $api<{ data: FormWithQuestions[], metadata: PaginationMetadata }>(url, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/api/forms', error, { filters })
      throw error
    }
  }

  /**
   * Get a single form by ID with questions
   */
  const getForm = async (id: string) => {
    try {
      return await $api<{ data: FormWithQuestions }>(`/api/forms/${id}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError(`/api/forms/${id}`, error, { id })
      throw error
    }
  }

  /**
   * Create a new form
   */
  const createForm = async (data: FormInsert) => {
    try {
      return await $api<{ data: Form }>('/api/forms', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/forms', error, { data })
      throw error
    }
  }

  /**
   * Update an existing form
   */
  const updateForm = async (id: string, updates: FormUpdate) => {
    try {
      return await $api<{ data: Form }>(`/api/forms/${id}`, {
        method: 'PATCH',
        body: updates,
      })
    }
    catch (error) {
      logApiError(`/api/forms/${id}`, error, { id, updates })
      throw error
    }
  }

  /**
   * Delete a form
   */
  const deleteForm = async (id: string) => {
    return await $api<{ success: boolean }>(`/api/forms/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get active forms (for use in camp creation)
   */
  const getActiveForms = async () => {
    return await getForms({ status: 'active', limit: 100 })
  }

  /**
   * Get template forms (reusable forms)
   */
  const getTemplateForms = async () => {
    return await getForms({ isTemplate: true, status: 'active', limit: 100 })
  }

  /**
   * Batch update questions (replaces all questions)
   */
  const batchUpdateQuestions = async (formId: string, questions: Partial<FormQuestion>[]) => {
    return await $api<{ data: FormQuestion[] }>(`/api/forms/${formId}/questions/batch`, {
      method: 'POST',
      body: { questions },
    })
  }

  /**
   * Duplicate a form
   */
  const duplicateForm = async (formId: string, newName: string) => {
    const { data: originalForm } = await getForm(formId)

    // Create new form
    const { data: newForm } = await createForm({
      name: newName,
      description: originalForm.description,
      status: 'active',
      is_template: originalForm.is_template,
    })

    // Copy questions if any
    if (originalForm.vzw_form_questions && originalForm.vzw_form_questions.length > 0) {
      const duplicatedQuestions = originalForm.vzw_form_questions.map(question => ({
        question_type: question.question_type,
        label: question.label,
        description: question.description,
        placeholder: question.placeholder,
        is_required: question.is_required,
        order_index: question.order_index,
        options: question.options,
        validation_rules: question.validation_rules,
        conditional_rule: question.conditional_rule,
        width: question.width,
      }))

      await batchUpdateQuestions(newForm.id, duplicatedQuestions)
    }

    return newForm
  }

  /**
   * Add a question to a form
   */
  const addQuestion = async (formId: string, question: Partial<FormQuestion>) => {
    return await $api<{ data: FormQuestion }>(`/api/forms/${formId}/questions`, {
      method: 'POST',
      body: question,
    })
  }

  /**
   * Update a question
   */
  const updateQuestion = async (formId: string, questionId: string, updates: Partial<FormQuestion>) => {
    return await $api<{ data: FormQuestion }>(`/api/forms/${formId}/questions/${questionId}`, {
      method: 'PATCH',
      body: updates,
    })
  }

  /**
   * Delete a question
   */
  const deleteQuestion = async (formId: string, questionId: string) => {
    return await $api<{ success: boolean }>(`/api/forms/${formId}/questions/${questionId}`, {
      method: 'DELETE',
    })
  }

  /**
   * Reorder questions
   */
  const reorderQuestions = async (formId: string, questionIds: string[]) => {
    const { data: form } = await getForm(formId)

    const reorderedQuestions = questionIds.map((id, index) => {
      const question = form.vzw_form_questions.find((q: FormQuestion) => q.id === id)

      return {
        ...question,
        order_index: index,
      }
    })

    return await batchUpdateQuestions(formId, reorderedQuestions)
  }

  return {
    getForms,
    getForm,
    createForm,
    updateForm,
    deleteForm,
    getActiveForms,
    getTemplateForms,
    duplicateForm,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    batchUpdateQuestions,
    reorderQuestions,
  }
}
